import Mention from '@tiptap/extension-mention'
import type { SuggestionOptions } from '@tiptap/suggestion'

import type { MentionItem } from '../../markdown-editor.interface'
import { createSuggestionRenderer } from './mention-suggestion-renderer.util'

export interface MentionExtensionConfig {
  items: MentionItem[]
  trigger?: string
  onSearch?: (query: string) => MentionItem[] | Promise<MentionItem[]>
  onSelect?: (item: MentionItem) => void
}

const defaultSearch = (itemList: MentionItem[], query: string): MentionItem[] => {
  const lowerQuery = query.toLowerCase()
  return itemList.filter(
    (item) =>
      item.label.toLowerCase().includes(lowerQuery) ||
      item.category?.toLowerCase().includes(lowerQuery) ||
      item.description?.toLowerCase().includes(lowerQuery)
  )
}

const createSuggestion = (
  config: MentionExtensionConfig
): Omit<SuggestionOptions<MentionItem>, 'editor'> => ({
  char: config.trigger ?? '@',
  allowSpaces: false,
  items: ({ query }: { query: string }) => {
    if (config.onSearch) {
      return config.onSearch(query)
    }
    return defaultSearch(config.items, query)
  },
  render: () => createSuggestionRenderer({ onSelect: config.onSelect }),
})

const mentionHtmlAttributes = { class: 'mention' }

export const createMentionExtension = (config: MentionExtensionConfig) =>
  Mention.configure({
    HTMLAttributes: mentionHtmlAttributes,
    suggestion: createSuggestion(config),
    renderHTML: ({ options, node }) => [
      'span',
      {
        ...options.HTMLAttributes,
        'data-type': 'mention',
        'data-id': node.attrs.id,
        'data-label': node.attrs.label,
        'data-category': node.attrs.category ?? '',
        'data-href': node.attrs.href ?? '',
      },
      `@${node.attrs.label}`,
    ],
  })
